import _ from 'lodash';
import React from 'react';
import gql from 'graphql-tag';
import graphqlLib from '../../lib/graphql-lib';
import { graphql, compose } from 'react-apollo';
import { TRANSFER_TYPE_OPTIONS } from '../../constants/FTPConfigOptions';
import {
	Pane,
	Form,
	FormControl,
	Input,
	Icon,
	Dropdown,
	Checkbox,
	Button,
	Alert,
} from '../../components';

import '../../../scss/containers/builders/_add-edit-pane.scss';

const initialState = {
	errors: [],
	fieldErrors: {
		server: false,
		remoteLogin: false,
		remotePassword: false,
		serverNameErrorMessage: null,
	},
	isDeleting: false,
	isDeleteConfirmationVisible: false,
	isEditing: false,
	isSaving: false,
	leadsFTPConfig: {},
};

export class EditFTPConfigPane extends Pane {
	state = initialState;

	componentWillReceiveProps(props) {
		const leadsFTPConfig = props.corporation.leadsFTPConfig
			? _.omit(props.corporation.leadsFTPConfig, ['id'])
			: {
					corporationId: props.corporation.id,
					ftpFolder: '',
					transferType: TRANSFER_TYPE_OPTIONS[0].value,
					feedHasEmailEnabled: true,
			  };

		this.setState({ leadsFTPConfig });
	}

	close() {
		// Reset the state, then close the pane
		this.setState(initialState, () => super.close());
	}

	setData(data) {
		// Validate the data
		const serverNameRegex = /^[a-zA-Z0-9][a-zA-Z0-9.-]*(?::[1-9][0-9]*)?$/;
		const fieldErrors = _.reduce(
			['server', 'remoteLogin', 'remotePassword'],
			(result, key) => {
				result[key] = data[key] === '';

				if (key === 'server') {
					const isValidServer = serverNameRegex.test(data[key]);
					result[key] = !isValidServer;
					result.serverNameErrorMessage =
						isValidServer || data[key] === '' ? null : 'Please enter valid server name';
				}
				return result;
			},
			{ ...this.state.fieldErrors },
		);

		// Update the state
		let leadsFTPConfig = _.assign({}, this.state.leadsFTPConfig, data);

		this.setState({ leadsFTPConfig, fieldErrors, isEditing: true });
	}

	async handleDelete(e) {
		e.preventDefault();

		const { id } = this.props.corporation.leadsFTPConfig;

		this.setState({ isDeleting: true });

		await this.props.deleteLeadsFTPConfig(id);

		this.setState(initialState, () => this.props.onSave());
	}

	async handleSubmitForm(e) {
		e.preventDefault();

		// Validate the form
		const { server, remoteLogin, remotePassword } = this.state.leadsFTPConfig;

		if (!server || !remoteLogin || !remotePassword) {
			this.setState({
				fieldErrors: {
					server: !server,
					remoteLogin: !remoteLogin,
					remotePassword: !remotePassword,
				},
			});
			return;
		}

		// Save the form
		const input = this.state.leadsFTPConfig;

		this.setState({ isSaving: true, errors: [] });

		try {
			if (!this.props.corporation.leadsFTPConfig) {
				await this.props.createLeadsFTPConfig(input);
			} else {
				await this.props.updateLeadsFTPConfig(input);
			}

			this.setState(initialState, () => this.props.onSave());
		} catch (err) {
			const errors = err.graphQLErrors
				? err.graphQLErrors.map(error => error.message)
				: ['Could not save FTP config'];

			this.setState({ isSaving: false, errors });
		}
	}

	renderHeader() {
		const { isDeleteConfirmationVisible } = this.state;

		return (
			<h5>
				<a href="#" onClick={e => this.handleClickClose(e)}>
					<Icon type="close" />
				</a>
				{isDeleteConfirmationVisible ? 'Delete Lead XML Feed Config' : this.props.title}
			</h5>
		);
	}

	renderDeleteConfirmation() {
		const { isDeleting } = this.state;

		return (
			<div className="container builder-add-edit-pane">
				<div className="ftp-config-delete-confirmation-text">
					Are you sure you want to delete Lead XML Config for Feed ID:{' '}
					{this.props.corporation.paidBuilderId}?
				</div>
				<div className="ftp-config-delete-confirmation-button-container">
					<Button
						type="button"
						size="small"
						className="pull-right"
						disabled={isDeleting}
						style={'save-ftp-config'}
						onClick={e => this.handleDelete(e)}>
						{isDeleting ? 'Deleting...' : 'Confirm'}
					</Button>
					<Button
						type="button"
						size="small"
						className="pull-right"
						disabled={isDeleting}
						style={'delete-ftp-config'}
						onClick={() => {
							this.setState({ isDeleteConfirmationVisible: false });
						}}>
						Cancel
					</Button>
				</div>
			</div>
		);
	}

	renderForm() {
		const { leadsFTPConfig, errors, fieldErrors, isEditing, isSaving } = this.state;
		const paidBuilderId = this.props.corporation.paidBuilderId;
		const hasValidInputs = _.every(fieldErrors, field => !field);
		const errorMessage = 'This field is required';

		return (
			<Form onSubmit={e => this.handleSubmitForm(e)}>
				<If condition={errors.length}>
					{errors.map(error => (
						<Alert key={index} type="dismiss" style="error">
							{error}
						</Alert>
					))}
				</If>

				<FormControl>
					<label>
						Paid Builder ID <span className="required">*</span>
					</label>
					<Input name="paidBuilderId" value={paidBuilderId || ''} disabled={true} />
				</FormControl>

				<FormControl hasError={fieldErrors.server}>
					<label>
						FTP Server Name <span className="required">*</span>
					</label>
					<Input
						name="ftpServer"
						value={leadsFTPConfig.server || ''}
						onChange={e => this.setData({ server: e.target.value })}
					/>
					<If condition={fieldErrors.server}>
						<Alert type="tooltip" style="error">
							{fieldErrors.serverNameErrorMessage || errorMessage}
						</Alert>
					</If>
				</FormControl>

				<FormControl>
					<label>FTP Folder Name</label>
					<Input
						name="ftpFolder"
						value={leadsFTPConfig.ftpFolder || ''}
						onChange={e => this.setData({ ftpFolder: e.target.value })}
					/>
				</FormControl>

				<FormControl hasError={fieldErrors.remoteLogin}>
					<label>
						FTP Login <span className="required">*</span>
					</label>
					<Input
						name="ftpLogin"
						value={leadsFTPConfig.remoteLogin || ''}
						onChange={e => this.setData({ remoteLogin: e.target.value })}
					/>
					<If condition={fieldErrors.remoteLogin}>
						<Alert type="tooltip" style="error">
							{errorMessage}
						</Alert>
					</If>
				</FormControl>

				<FormControl hasError={fieldErrors.remotePassword}>
					<label>
						FTP Password <span className="required">*</span>
					</label>
					<Input
						name="ftpPassword"
						value={leadsFTPConfig.remotePassword || ''}
						onChange={e => this.setData({ remotePassword: e.target.value })}
					/>
					<If condition={fieldErrors.remotePassword}>
						<Alert type="tooltip" style="error">
							{errorMessage}
						</Alert>
					</If>
				</FormControl>

				<FormControl>
					<label>
						FTP Transfer Mode <span className="required">*</span>
					</label>
					<Dropdown
						name="transferType"
						options={TRANSFER_TYPE_OPTIONS}
						selected={leadsFTPConfig.transferType}
						onChange={transferType => this.setData({ transferType })}
					/>
				</FormControl>

				<FormControl>
					<label>Keep Email On?</label>
					<Checkbox
						name="isEmailEnabled"
						defaultChecked={leadsFTPConfig.feedHasEmailEnabled}
						onChange={e =>
							this.setData({
								feedHasEmailEnabled: !leadsFTPConfig.feedHasEmailEnabled,
							})
						}
					/>
				</FormControl>

				<Button
					type="submit"
					size="small"
					className="pull-right"
					disabled={!isEditing || !hasValidInputs || isSaving}
					style={'save-ftp-config'}>
					{isSaving
						? 'Saving...'
						: this.props.corporation.leadsFTPConfig
						? 'Update'
						: 'Add'}
				</Button>

				<If condition={this.props.corporation.leadsFTPConfig}>
					<Button
						type="button"
						size="small"
						className="pull-right"
						disabled={isSaving}
						style={'delete-ftp-config'}
						onClick={() => {
							this.setState({ isDeleteConfirmationVisible: true });
						}}>
						Delete
					</Button>
				</If>
			</Form>
		);
	}

	renderContent() {
		const { isDeleteConfirmationVisible } = this.state;

		return isDeleteConfirmationVisible ? this.renderDeleteConfirmation() : this.renderForm();
	}
}

const createFTPConfigMutation = gql`
	mutation CreateLeadsFTPConfig($input: CreateLeadsFTPConfigInput!) {
		createLeadsFTPConfig(input: $input) {
			id
		}
	}
`;

const updateFTPConfigMutation = gql`
	mutation UpdateLeadsFTPConfig($input: UpdateLeadsFTPConfigInput!) {
		updateLeadsFTPConfig(input: $input) {
			id
		}
	}
`;

const deleteFTPConfigMutation = gql`
	mutation DeleteLeadsFTPConfig($id: EncodedID!) {
		deleteLeadsFTPConfig(id: $id) {
			id
		}
	}
`;

export default compose(
	graphql(createFTPConfigMutation, {
		props: ({ mutate }) => ({
			createLeadsFTPConfig: input =>
				mutate({
					variables: {
						input: graphqlLib.removeTypename(input),
					},
				}),
		}),
	}),
	graphql(updateFTPConfigMutation, {
		props: ({ mutate }) => ({
			updateLeadsFTPConfig: input =>
				mutate({
					variables: {
						input: graphqlLib.removeTypename(input),
					},
				}),
		}),
	}),
	graphql(deleteFTPConfigMutation, {
		props: ({ mutate }) => ({
			deleteLeadsFTPConfig: id =>
				mutate({
					variables: {
						id,
					},
				}),
		}),
	}),
)(EditFTPConfigPane);



// WEBPACK FOOTER //
// ./src/assets/js/containers/builders/AddEditPane.js